import { useReducer, useRef, useEffect } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { PlayIcon, PauseIcon } from "@heroicons/react/20/solid";
import { LoaderCircle } from "lucide-react";
import { toast } from "sonner";

export interface VoiceMessageRecording {
  content_type: string;
  type: "audio";
  data: Blob;
  name: string;
}

interface AudioRecorderProps {
  onAudioRecorded: (payload: VoiceMessageRecording) => void;
  onCancel: () => void;
  maxDuration?: number; // in milliseconds
}

// State machine types
type RecordingStatus =
  | "idle"
  | "requesting"
  | "recording"
  | "paused"
  | "finished"
  | "error";

interface RecordingState {
  status: RecordingStatus;
  recordingTime: number;
  audioData: Blob | null;
  visualizerData: number[];
  errorMessage: string | null;
  submitted: boolean; // Track if the recording has been submitted
}

type RecordingAction =
  | { type: "PERMISSION_REQUESTED" }
  | { type: "PERMISSION_GRANTED" }
  | { type: "PERMISSION_DENIED"; payload: string }
  | { type: "RECORDING_STARTED" }
  | { type: "RECORDING_PAUSED" }
  | { type: "RECORDING_RESUMED" }
  | { type: "UPDATE_TIME"; payload: number }
  | { type: "UPDATE_VISUALIZER"; payload: number[] }
  | { type: "MAX_DURATION_REACHED" }
  | { type: "RECORDING_FINISHED"; payload: Blob }
  | { type: "RECORDING_SUBMITTED" }
  | { type: "RECORDING_CANCELLED" };

// Utility function for MIME type detection
const getMimeType = () => {
  if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
    return "audio/webm;codecs=opus";
  }
  if (MediaRecorder.isTypeSupported("audio/webm")) {
    return "audio/webm";
  }
  if (MediaRecorder.isTypeSupported("audio/mp3")) {
    return "audio/mp3";
  }
  if (MediaRecorder.isTypeSupported("audio/mp4")) {
    return "audio/mp4";
  }
  return "";
};

// Helper function to determine file extension from mime type
const getFileExtension = (mimeType: string): string => {
  if (mimeType.includes("mp3")) {
    return "mp3";
  } else if (mimeType.includes("mp4") || mimeType.includes("mp4a")) {
    return "mp4";
  } else if (mimeType.includes("wav")) {
    return "wav";
  }
  return "webm"; // Default
};

// Custom hook for audio recording functionality
function useAudioRecorder(
  maxDuration: number,
  dispatch: React.Dispatch<RecordingAction>
) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  const startTimeRef = useRef<number>(0);
  const recordedDurationRef = useRef<number>(0);

  const mimeType = getMimeType();

  const startRecording = async () => {
    dispatch({ type: "PERMISSION_REQUESTED" });

    // Check Microphone Access. We use the access to directly go to next step and prevent flickering if already granted.
    try {
      const permisson = await navigator.permissions.query({
        name: "microphone",
      });
      if (permisson.state === "denied") {
        dispatch({
          type: "PERMISSION_DENIED",
          payload: "Microphone access denied",
        });
        return null;
      } else if (permisson.state === "granted") {
        dispatch({ type: "PERMISSION_GRANTED" });
      } else if (permisson.state === "prompt") {
        // Continue to next step
      }
    } catch (error) {
      console.error("Error checking microphone permission:", error);
      // but continue anyway; permissions API may not be supported by mediaDevices can be.
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      mediaRecorder.addEventListener("stop", () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        dispatch({ type: "RECORDING_FINISHED", payload: audioBlob });
        cleanup();
      });

      // Start recording
      mediaRecorder.start(100);

      // Reset timing references
      recordedDurationRef.current = 0;
      startTimeRef.current = Date.now();

      // Start timer
      startTimer();

      dispatch({ type: "PERMISSION_GRANTED" });
      dispatch({ type: "RECORDING_STARTED" });

      // Return the stream so it can be used for the visualizer
      return stream;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      dispatch({
        type: "PERMISSION_DENIED",
        payload: error instanceof Error ? error.message : "Unknown error",
      });
      return null;
    }
  };

  // Timer function
  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    startTimeRef.current = Date.now();

    timerRef.current = window.setInterval(() => {
      const currentDuration =
        recordedDurationRef.current + (Date.now() - startTimeRef.current);
      dispatch({ type: "UPDATE_TIME", payload: currentDuration });

      // Check for max duration
      if (currentDuration >= maxDuration) {
        dispatch({ type: "MAX_DURATION_REACHED" });
        pauseRecording();
      }
    }, 100);
  };

  // Pause recording function
  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      // Update recorded duration
      recordedDurationRef.current += Date.now() - startTimeRef.current;

      dispatch({ type: "RECORDING_PAUSED" });
    }
  };

  // Resume recording function
  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      startTimer();
      dispatch({ type: "RECORDING_RESUMED" });
    }
  };

  // Stop recording function
  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  };

  // Cleanup function
  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return cleanup;
  }, []);

  return {
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cleanup,
    mimeType,
    getStream: () => streamRef.current,
  };
}

// Custom hook for audio visualization
function useAudioVisualizer(
  status: RecordingStatus,
  dispatch: React.Dispatch<RecordingAction>
) {
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isVisualizerActive = useRef<boolean>(false);

  // Setup audio context and analyser
  const setupVisualizer = (stream: MediaStream) => {
    if (!stream) return;

    // Create audio context and analyser only once
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
    }

    isVisualizerActive.current = true;
    startVisualization();
  };

  // Start visualization
  const startVisualization = () => {
    if (!analyserRef.current || !isVisualizerActive.current) return;

    // Stop any existing animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const updateVisualizer = () => {
      if (analyserRef.current && isVisualizerActive.current) {
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Take a sample of data points for visualization
        const sampleSize = 20;
        const samples = Array.from({ length: sampleSize }, (_, i) => {
          const index = Math.floor(i * (dataArray.length / sampleSize));
          return dataArray[index];
        });

        // Only update visualizer when recording (not when paused)
        // For paused state, we keep the last visualization data
        if (status === "recording") {
          dispatch({ type: "UPDATE_VISUALIZER", payload: samples });
        }

        // Continue animation frame loop if visualizer is active
        animationFrameRef.current = requestAnimationFrame(updateVisualizer);
      }
    };

    // Start the visualizer
    updateVisualizer();
  };

  // Pause visualization (keeps last state but stops updates)
  const pauseVisualization = () => {
    isVisualizerActive.current = false;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  // Resume visualization
  const resumeVisualization = () => {
    isVisualizerActive.current = true;
    startVisualization();
  };

  // Stop and cleanup visualization resources
  const stopVisualization = () => {
    isVisualizerActive.current = false;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.error);
      audioContextRef.current = null;
    }

    analyserRef.current = null;
  };

  // Update visualization based on status changes
  useEffect(() => {
    if (status === "recording") {
      resumeVisualization();
    } else if (status === "paused") {
      pauseVisualization();
    } else if (
      status === "finished" ||
      status === "idle" ||
      status === "error"
    ) {
      stopVisualization();
    }
  }, [status]);

  // Clean up on unmount
  useEffect(() => {
    return stopVisualization;
  }, []);

  return {
    setupVisualizer,
    stopVisualization,
  };
}

// Reducer function for state management
function recordingReducer(
  state: RecordingState,
  action: RecordingAction
): RecordingState {
  switch (action.type) {
    case "PERMISSION_REQUESTED":
      return { ...state, status: "requesting" };

    case "PERMISSION_GRANTED":
      return { ...state, status: "idle" };

    case "PERMISSION_DENIED":
      return {
        ...state,
        status: "error",
        errorMessage: action.payload,
      };

    case "RECORDING_STARTED":
      return {
        ...state,
        status: "recording",
        recordingTime: 0,
        audioData: null,
        submitted: false,
      };

    case "RECORDING_PAUSED":
      return { ...state, status: "paused" };

    case "RECORDING_RESUMED":
      return { ...state, status: "recording" };

    case "UPDATE_TIME":
      return { ...state, recordingTime: action.payload };

    case "UPDATE_VISUALIZER":
      return { ...state, visualizerData: action.payload };

    case "MAX_DURATION_REACHED":
      toast.success("Maximum recording duration reached", {
        duration: 6000,
      });
      return { ...state, status: "paused" };

    case "RECORDING_FINISHED":
      return {
        ...state,
        status: "finished",
        audioData: action.payload,
        submitted: false,
      };

    case "RECORDING_SUBMITTED":
      return {
        ...state,
        submitted: true,
      };

    case "RECORDING_CANCELLED":
      return {
        ...state,
        status: "idle",
        recordingTime: 0,
        audioData: null,
        submitted: false,
      };

    default:
      return state;
  }
}

// UI Component: Time Display
const TimeDisplay = ({ time }: { time: number }) => {
  // Format time display (MM:SS)
  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="text-sm text-chat-text-light w-12 text-center flex-shrink-0">
      {formatTime(time)}
    </div>
  );
};

// UI Component: Visualizer
const VisualizerDisplay = ({
  data,
  isPaused,
}: {
  data: number[];
  isPaused: boolean;
}) => {
  return (
    <div className="flex-grow h-6 flex items-center justify-center gap-0.5 max-w-xs">
      {data.map((value, index) => (
        <div
          key={index}
          className={`w-1 rounded-full ${isPaused ? "bg-gray-400" : "bg-brand"}`}
          style={{
            height: `${Math.max(2, (value / 255) * 20)}px`,
          }}
        />
      ))}
    </div>
  );
};

// Main AudioRecorder Component
export function AudioRecorder({
  onAudioRecorded,
  onCancel,
  maxDuration = 30 * 60 * 1000,
}: AudioRecorderProps) {
  // Initialize state with useReducer
  const [state, dispatch] = useReducer(recordingReducer, {
    status: "idle",
    recordingTime: 0,
    audioData: null,
    visualizerData: Array(20).fill(2),
    errorMessage: null,
    submitted: false,
  });

  // Use custom hooks
  const recorder = useAudioRecorder(maxDuration, dispatch);
  const visualizer = useAudioVisualizer(state.status, dispatch);

  // Effect to start recording when component mounts
  useEffect(() => {
    const startUp = async () => {
      const stream = await recorder.startRecording();

      // Set up visualizer with the stream once available
      if (stream) {
        visualizer.setupVisualizer(stream);
      }
    };

    startUp();
  }, []);

  // Handle toggle recording
  const toggleRecording = () => {
    if (state.status === "paused") {
      recorder.resumeRecording();
    } else if (state.status === "recording") {
      recorder.pauseRecording();
    }
  };

  // Handle cancel recording
  const cancelRecording = () => {
    dispatch({ type: "RECORDING_CANCELLED" });

    // Then clean up resources
    recorder.cleanup();
    onCancel();
  };

  // Handle submit recording
  const handleSubmit = () => {
    if (state.status === "recording" || state.status === "paused") {
      // If recording or paused, just stop recording
      // This will trigger the mediaRecorder's stop event
      // which will set the status to "finished"
      recorder.stopRecording();
    }
  };

  useEffect(() => {
    if (state.status === "finished" && state.audioData && !state.submitted) {
      const mimeType = state.audioData.type;
      const fileExtension = getFileExtension(mimeType);

      const audioPayload: VoiceMessageRecording = {
        content_type: mimeType,
        type: "audio",
        data: state.audioData,
        name: `voice.${fileExtension}`,
      };

      onAudioRecorded(audioPayload);
      dispatch({ type: "RECORDING_SUBMITTED" });
    }
  }, [state.status, state.audioData, state.submitted]);

  if (state.status === "requesting") {
    return (
      <div className="flex items-center w-full justify-between">
        <div className="flex-grow p-2 text-chat-text-light">
          Requesting microphone access...
        </div>
        <div className="flex">
          <button
            type="button"
            onClick={cancelRecording}
            className="p-2.5 text-chat-text hover:bg-slate-200 transition-colors rounded-full"
            aria-label="Cancel"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex items-center justify-between w-full text-red-500">
        <span className="text-sm px-4">
          Microphone access denied. Review settings.
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="p-2.5 text-chat-text hover:bg-slate-200 rounded-full"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center w-full justify-between gap-2">
      {/* Trash button (left side) */}
      <button
        type="button"
        onClick={cancelRecording}
        className="p-2.5 text-gray-600 hover:text-red-500 hover:bg-red-100 rounded-full transition-colors flex-shrink-0"
        aria-label="Cancel recording"
      >
        <TrashIcon className="h-5 w-5" />
      </button>

      {/* Time display */}
      <TimeDisplay time={state.recordingTime} />

      {/* Visualizer */}
      <VisualizerDisplay
        data={state.visualizerData}
        isPaused={state.status === "paused"}
      />

      {/* Play/Pause button */}
      {state.recordingTime >= maxDuration ? (
        <div className="w-8 h-8" />
      ) : (
        <button
          type="button"
          onClick={toggleRecording}
          disabled={state.status === "finished"}
          className={`flex items-center justify-center w-8 h-8 rounded-full disabled:cursor-not-allowed ${
            state.status === "paused"
              ? "bg-gray-200 text-gray-600"
              : "bg-red-500 text-white"
          } ${
            state.status === "finished"
              ? "cursor-not-allowed opacity-70"
              : "hover:opacity-90 cursor-pointer"
          } transition-colors flex-shrink-0`}
          aria-label={
            state.status === "paused" ? "Resume recording" : "Pause recording"
          }
        >
          {state.status === "paused" || state.status === "finished" ? (
            <PlayIcon className="h-4 w-4" />
          ) : (
            <PauseIcon className="h-4 w-4" />
          )}
        </button>
      )}

      {/* Submit button */}
      <button
        type="button"
        onClick={handleSubmit}
        className="p-3 text-chat bg-brand hover:bg-brand-hover transition-colors rounded-full flex-shrink-0"
        disabled={state.status === "finished"}
        aria-label={
          state.status !== "finished"
            ? "Stop recording"
            : "Send audio recording"
        }
      >
        {state.status === "finished" ? (
          <LoaderCircle className="animate-spin h-4 w-4 text-white" />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="none"
            className="h-4 w-4"
            strokeWidth="2"
          >
            <path
              d="M.5 1.163A1 1 0 0 1 1.97.28l12.868 6.837a1 1 0 0 1 0 1.766L1.969 15.72A1 1 0 0 1 .5 14.836V10.33a1 1 0 0 1 .816-.983L8.5 8 1.316 6.653A1 1 0 0 1 .5 5.67V1.163Z"
              fill="currentColor"
            />
          </svg>
        )}
      </button>
    </div>
  );
}
